import { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Download, Upload, CheckCircle2, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export const CargaMasivaDialog = ({ open, onOpenChange, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleDescargarPlantilla = async () => {
    try {
      const r = await axios.get(`${API}/propiedades-plantilla-csv`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "plantilla_propiedades.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al descargar la plantilla");
    }
  };

  const handleSubir = async () => {
    if (!file) return toast.error("Selecciona un archivo CSV");
    setSaving(true);
    setResultado(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/propiedades/carga-masiva`, fd, { headers: authHeader() });
      setResultado(r.data);
      if (r.data.propiedades_creadas > 0) {
        toast.success(`${r.data.propiedades_creadas} propiedades creadas`);
        onSuccess();
      } else {
        toast.error("No se creó ninguna propiedad. Revisa los errores.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al procesar el CSV");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (v) => {
    if (!v) { setFile(null); setResultado(null); }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="carga-masiva-modal">
        <DialogHeader><DialogTitle>Carga masiva de propiedades (CSV)</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-sm text-gray-600 space-y-2">
            <p>1. Descarga la plantilla y llénala con tus propiedades (una por fila).</p>
            <p>2. Si el <strong>estado</strong> es <code className="bg-white px-1 rounded">rentada</code> e incluyes inquilino, renta y fechas, se creará también el <strong>contrato con su calendario de rentas</strong>.</p>
            <Button variant="outline" size="sm" onClick={handleDescargarPlantilla} data-testid="descargar-plantilla-btn">
              <Download className="w-4 h-4 mr-1" /> Descargar plantilla CSV
            </Button>
          </div>
          <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="csv-file-input" />

          {resultado && (
            <div className="space-y-2 text-sm" data-testid="carga-masiva-resultado">
              <p className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-4 h-4" /> {resultado.propiedades_creadas} propiedades creadas · {resultado.contratos_creados} contratos creados
              </p>
              {resultado.errores?.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-lg bg-amber-50 border border-amber-200 p-2 space-y-1">
                  {resultado.errores.map((e, i) => (
                    <p key={i} className="text-xs text-amber-800 flex gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Fila {e.fila}: {e.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>Cerrar</Button>
          <Button onClick={handleSubir} disabled={saving || !file} data-testid="subir-csv-btn">
            <Upload className="w-4 h-4 mr-1" /> {saving ? "Procesando..." : "Subir CSV"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
